import type { Language } from "@/Common/Models/Domain/Language";
import type { Locale } from "../Model/Domain";
import { getLocaleCacheStorageKey, LOCALE_STORAGE_SERVICE } from "../Model/Constants";

import { LocalStorageService } from "@/Services/LocalStorageService";

import { danger } from "@/Utils/Debug";
import { isObject } from "@/Utils/Objects";
import { normalizeLanguage } from "@/Utils/Language";

class LocaleCacheService {
  private readonly storage = new LocalStorageService(LOCALE_STORAGE_SERVICE);

  public get(locale: Language): Locale | undefined {
    const storageKey = getLocaleCacheStorageKey(locale);

    try {
      const cachedLocale = this.storage.get<unknown>(storageKey);

      if (cachedLocale === undefined || cachedLocale === null) return;

      if (!this.isValidLocale(cachedLocale, locale)) {
        this.delete(locale);
        danger("INVALID LOCALE CACHE", locale);

        return;
      }

      return cachedLocale;
    } catch (error) {
      this.delete(locale);
      danger("READ LOCALE CACHE ERROR", locale, error);

      return;
    }
  }

  public set(locale: Locale): void {
    try {
      this.storage.set(getLocaleCacheStorageKey(locale.locale), locale);
    } catch (error) {
      danger("WRITE LOCALE CACHE ERROR", locale.locale, error);
    }
  }

  public delete(locale: Language): void {
    try {
      this.storage.delete(getLocaleCacheStorageKey(locale));
    } catch (error) {
      danger("DELETE LOCALE CACHE ERROR", locale, error);
    }
  }

  private isValidLocale(value: unknown, locale: Language): value is Locale {
    if (!isObject(value)) return false;

    const translations = value.translations;

    return (
      normalizeLanguage(value.locale) === locale &&
      typeof value.version === "number" &&
      Number.isFinite(value.version) &&
      isObject(translations) &&
      !Array.isArray(translations) &&
      Object.values(translations).every((translation) => {
        return typeof translation === "string" || translation === null;
      })
    );
  }
}

export default new LocaleCacheService();
